import { api } from '@/api/client';
import type { EmployeeQueryParams, EmployeeResponse, ListJoinRequestsResponse, CreateEmployeeData, JoinRequestResponse } from '../types';
import type { ApiError } from '@/api/client';

export async function fetchEmployees(
  params: EmployeeQueryParams
): Promise<EmployeeResponse> {
  return api.get<EmployeeResponse>(`/v1/companies/${params.companyId}/employees`, {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
    },
  });
}

export async function fetchJoinRequests(
  companyId: string
): Promise<ListJoinRequestsResponse> {
  return api.get<ListJoinRequestsResponse>(`/v1/companies/${companyId}/join-requests`);
}

export async function fetchJoinRequest(
  companyId: string,
  requestId: string
): Promise<JoinRequestResponse> {
  return api.get<JoinRequestResponse>(`/v1/companies/${companyId}/join-request/${requestId}`);
}

export async function acceptJoinRequest(
  companyId: string,
  requestId: string,
  data: { contact: CreateEmployeeData } | { contactId: string }
): Promise<void> {
  return api.post(`/v1/companies/${companyId}/join-request/${requestId}/accept`, data);
}

export async function declineJoinRequest(
  companyId: string,
  requestId: string
): Promise<void> {
  return api.post(`/v1/companies/${companyId}/join-request/${requestId}/reject`);
}

export async function sendInviteLink(
  companyId: string,
  contactId: string,
  email: string
): Promise<void> {
  return api.post(`/v1/companies/${companyId}/contacts/${contactId}/invite`, { email });
}

export async function updatePrimaryContact(
  companyId: string,
  contactId: string,
  isPrimary: boolean
): Promise<void> {
  return api.patch(`/v1/companies/${companyId}/contacts/${contactId}`, { isPrimary });
}

export interface UserRole {
  id: string;
  name: string;
}

export async function fetchUserRoles(
  companyId: string,
  locale: string
): Promise<UserRole[]> {
  return api.get<UserRole[]>(`/v1/companies/${companyId}/roles`, {
    params: { locale },
  });
}

export async function fetchGlobalUserRoles(locale: string): Promise<UserRole[]> {
  return api.get<UserRole[]>(`/v1/user/roles`, { params: { locale } });
}

export interface CreateEmployeeRequest {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  email: string;
  roleId: string;
  isMainContact: boolean;
  userType: 'default' | 'managed';
  // Either an invite email for a new account or the id of an existing user
  userEmail?: string;
  userId?: string;
}

export async function createEmployee(
  companyId: string,
  data: CreateEmployeeRequest
): Promise<EmployeeCreateResponse> {
  try {
    await api.post(`/v1/companies/${companyId}/employees`, data);
    return EmployeeCreateResponse.OK;
  } catch (e) {
    const error = e as ApiError;

    console.log(error, e);

    if (error === null) {
      throw e;
    }

    if (error?.status === 409) {
      return EmployeeCreateResponse.EMAIL_EXISTS;
    }

    return EmployeeCreateResponse.UNKNOWN_ERROR;
  }
}



export enum EmployeeCreateResponse {
  EMAIL_EXISTS = 'EMAIL_EXISTS',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  OK = 'OK'
}